import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { AlertTriangle, Trash2, Loader2 } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

/**
 * Re-usable confirmation dialog for soft-deleting an entity.
 * Asks the admin to pick a reason from a server-supplied list (Other = freeform).
 *
 * Props
 * -----
 *  - open, onClose
 *  - title          (string)   - heading
 *  - description    (string)   - body text describing what's being deleted
 *  - entityLabel    (string)   - shown in the confirm button (e.g. "job")
 *  - onConfirm      (function) - async ({ reason_id, reason_label, reason_note }) => void
 *  - dangerLevel    ('soft' | 'permanent')  - 'permanent' adds extra warning UI
 */
export default function DeleteWithReasonDialog({
  open,
  onClose,
  title = 'Move to archive',
  description = 'This will soft-delete the item and move it to the Archive page where it can be restored.',
  entityLabel = 'item',
  onConfirm,
  dangerLevel = 'soft',
}) {
  const [reasons, setReasons] = useState([]);
  const [selected, setSelected] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isPermanent = dangerLevel === 'permanent';

  useEffect(() => {
    if (!open) return;
    setSelected('');
    setNote('');
    setLoading(true);
    const token = localStorage.getItem('token');
    axios.get(`${API}/archive/reasons`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setReasons(r.data.reasons || []))
      .catch(() => setReasons([
        { id: 'completed_and_invoiced', label: 'Completed and invoiced' },
        { id: 'canceled_by_customer', label: 'Cancelled by customer' },
        { id: 'duplicate', label: 'Duplicate entry' },
        { id: 'test_or_error_entry', label: 'Test / created in error' },
        { id: 'no_longer_needed', label: 'No longer needed' },
        { id: 'assigned_to_other_company', label: 'Assigned to another company' },
        { id: 'lost_bid_or_declined', label: 'Lost bid / customer declined' },
        { id: 'other', label: 'Other (specify)' },
      ]))
      .finally(() => setLoading(false));
  }, [open]);

  const isOtherChosen = selected === 'other';
  const canSubmit =
    !!selected &&
    !submitting &&
    (!isOtherChosen || note.trim().length >= 3);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const reasonObj = reasons.find((r) => r.id === selected);
      // For "other", the reason value sent to backend = the freeform note text.
      // Otherwise the canonical id is sent so analytics can group by reason.
      const reasonValue = isOtherChosen ? note.trim() : selected;
      await onConfirm({
        reason_id: selected,
        reason_label: reasonObj?.label || selected,
        reason_note: isOtherChosen ? note.trim() : null,
        reason: reasonValue,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1A2433] text-white max-w-md" data-testid="delete-reason-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isPermanent ? (
              <AlertTriangle className="w-5 h-5 text-red-500" />
            ) : (
              <Trash2 className="w-5 h-5 text-amber-400" />
            )}
            {title}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm pt-1">
            {description}
          </DialogDescription>
        </DialogHeader>

        {isPermanent && (
          <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
            <strong className="block mb-1">This action cannot be undone.</strong>
            All data for this {entityLabel} will be permanently erased, including any
            related photos, files, and history.
          </div>
        )}

        <div className="space-y-3">
          <Label className="text-sm text-slate-300">Reason {isPermanent ? 'for permanent delete' : 'for archiving'}</Label>
          {loading ? (
            <div className="flex items-center text-slate-400 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading reasons…
            </div>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
              {reasons.map((r) => (
                <label
                  key={r.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer border ${
                    selected === r.id
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-[#2D3748] hover:border-blue-500/50'
                  }`}
                  data-testid={`reason-option-${r.id}`}
                >
                  <input
                    type="radio"
                    name="archive-reason"
                    value={r.id}
                    checked={selected === r.id}
                    onChange={() => setSelected(r.id)}
                    className="accent-blue-500"
                  />
                  <span className="text-sm">{r.label}</span>
                </label>
              ))}
            </div>
          )}

          {isOtherChosen && (
            <div className="space-y-1">
              <Label className="text-xs text-slate-400">Please explain the reason</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="A short description so we can audit later…"
                rows={3}
                className="bg-[#0F1923] border-[#2D3748] text-white"
                data-testid="reason-other-note"
              />
              <p className={`text-xs ${note.trim().length >= 3 ? 'text-slate-500' : 'text-amber-400'}`}>
                {note.trim().length < 3 ? 'Minimum 3 characters required.' : `${note.trim().length} characters`}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            data-testid="delete-reason-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={isPermanent ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
            data-testid="delete-reason-confirm"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Working…</>
            ) : isPermanent ? (
              <>Permanently delete {entityLabel}</>
            ) : (
              <>Move to archive</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
